import React, { useState } from 'react';
import { Bookmark, Ship, Star, Trash2, Eye, Search } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { VesselDrawer } from '@/components/vessel/VesselDrawer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useApp } from '@/context/AppContext';
import { vessels } from '@/data/mockData';

export default function BookmarkPage() {
  const { selectVessel } = useApp();
  const [bookmarkedVessels, setBookmarkedVessels] = useState(
    vessels.slice(0, 4).map(v => ({ ...v, bookmarkedAt: new Date().toISOString() }))
  );
  const [searchQuery, setSearchQuery] = useState('');

  const filteredBookmarks = bookmarkedVessels.filter(v =>
    v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.mmsi.includes(searchQuery)
  );

  const removeBookmark = (vesselId) => {
    setBookmarkedVessels(prev => prev.filter(v => v.id !== vesselId));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'underway':
        return 'border-success text-success';
      case 'at anchor':
        return 'border-primary text-primary';
      case 'moored':
        return 'border-info text-info';
      default:
        return 'border-muted text-muted-foreground';
    }
  };

  return (
    <DashboardLayout title="Bookmark">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Bookmark className="w-5 h-5 text-primary" />
                  Bookmarked Vessels
                </CardTitle>
                <CardDescription>
                  Quick access to vessels you're monitoring
                </CardDescription>
              </div>
              <Badge variant="outline">
                {bookmarkedVessels.length} vessels
              </Badge>
            </div>

            {/* Search */}
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cari bookmark..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </CardHeader>

          <CardContent className="space-y-3">
            {filteredBookmarks.length > 0 ? (
              filteredBookmarks.map((vessel) => (
                <div
                  key={vessel.id}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-accent/50 transition-colors group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Ship className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-foreground">{vessel.name}</h3>
                        <Star className="w-4 h-4 text-warning fill-warning" />
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-muted-foreground">
                          MMSI: {vessel.mmsi}
                        </span>
                        <Badge
                          variant="outline"
                          className={cn("text-xs", getStatusColor(vessel.status))}
                        >
                          {vessel.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Bookmarked: {formatDate(vessel.bookmarkedAt)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => selectVessel(vessel)}
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => removeBookmark(vessel.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <Bookmark className="w-16 h-16 mb-4 opacity-30" />
                <p className="text-sm">
                  {searchQuery ? 'No bookmarks match your search' : 'No bookmarked vessels yet'}
                </p>
                <p className="text-xs mt-1">
                  Click the bookmark icon on any vessel to add it here
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tips Card */}
        <Card className="bg-accent/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Star className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Pro Tip</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Bookmark vessels you frequently monitor for quick access. 
                  You can also receive notifications when bookmarked vessels enter or exit geofences.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <VesselDrawer />
    </DashboardLayout>
  );
}
